// cartons.js — the Cartons screen (V2 #100), on the backend's #94 and #99.
//
// The list is GET /library/browse with surface=control_center: the backend
// remembers the view (whose cartons) per user and per surface, and RECORDS
// every change of it (decision 5 on #92). So the view is sent only when the
// admin chooses one; a plain reload sends none and gets the remembered view.
// Filters, search, sort and pages are not a view: sent when set, never recorded.
//
// The actions are POST /admin/cartons/{id}/{unshare|delete|restore} with a
// reason, each recorded by the backend (#99). There is no rename.
//
// Transport-free: the request shapes and every word the screen shows are here,
// where node can test them (cartons.test.js).

export const SCOPES = [
  { value: 'mine', label: 'Mine and my team’s' },
  { value: 'org', label: 'One organisation' },
  { value: 'all', label: 'All organisations' },
];
export const STATUSES = [
  { value: '', label: 'Any' },
  { value: 'shared', label: 'Shared' },
  { value: 'private', label: 'Private' },
  { value: 'draft', label: 'Drafts' },
  { value: 'deleted', label: 'Deleted' },
];
export const KINDS = [
  { value: '', label: 'Any kind' },
  { value: 'pdf', label: 'PDF' },
  { value: 'two_slot', label: 'Two-file' },
  { value: 'parametric', label: 'Blank' },
];
export const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'name', label: 'By name' },
  { value: 'owner', label: 'By owner' },
];
export const PAGE = 50;
export const NO_FILTERS = { q: '', owner: null, ownerName: '', status: '', kind: '', duplicatesOnly: false, sort: 'newest' };

/** A view in its one canonical form, as the backend keeps it. */
export function normaliseView(view) {
  const v = view || {};
  if (v.scope === 'all' || (v.scope === 'org' && v.org_id)) {
    return { scope: v.scope, org_id: v.scope === 'org' ? v.org_id : null, include_drafts: !!v.include_drafts };
  }
  return { scope: 'mine', org_id: null, include_drafts: false };
}

export function sameView(a, b) {
  const x = normaliseView(a), y = normaliseView(b);
  return x.scope === y.scope && x.org_id === y.org_id && x.include_drafts === y.include_drafts;
}

/** The view in words, for the page's subtitle. */
export function viewLabel(view, orgsById = {}) {
  const v = normaliseView(view);
  if (v.scope === 'mine') return 'your own and your team’s';
  const where = v.scope === 'all' ? 'all organisations' : ((orgsById[v.org_id] || {}).name || 'one organisation');
  return v.include_drafts ? `${where}, other people’s drafts included` : where;
}

export const CARTON_ROUTES = {
  /** [path, opts] for api.js's request(). `view` only when just chosen. */
  browse({ view = null, filters = {}, page = 0, pageSize = PAGE } = {}) {
    const p = new URLSearchParams();
    p.set('surface', 'control_center');
    if (view) {
      const v = normaliseView(view);
      p.set('scope', v.scope);
      if (v.scope === 'org') p.set('org_id', v.org_id);
      if (v.scope !== 'mine') p.set('include_drafts', v.include_drafts ? 'true' : 'false');
    }
    const f = filters || {};
    const q = (f.q || '').trim();
    if (q) p.set('q', q);
    if (f.owner) p.set('owner', f.owner);
    if (f.status) p.set('status', f.status);
    if (f.kind) p.set('kind', f.kind);
    if (f.duplicatesOnly) p.set('duplicates_only', 'true');
    if (f.sort && f.sort !== 'newest') p.set('sort', f.sort);
    if (page > 0) p.set('page', String(page + 1));          // the backend counts from 1
    p.set('page_size', String(pageSize));
    return [`/library/browse?${p.toString()}`];
  },
  act(cartonId, action, reason) {
    if (!ACTIONS[action]) throw new Error(`unknown carton action ${action}`);
    return [`/admin/cartons/${encodeURIComponent(cartonId)}/${action}`,
      { method: 'POST', body: { reason: (reason || '').trim() } }];
  },
};

/* ── what a row says ──────────────────────────────────────────────── */

export function statusOf(c) {
  if (c.deleted_at) return 'deleted';
  if (c.origin === 'draft') return 'draft';
  return c.visibility === 'org' ? 'shared' : 'private';
}

export const STATUS_WORDS = {
  shared: { label: 'Shared', tone: 'ok' },
  private: { label: 'Private', tone: '' },
  draft: { label: 'Draft', tone: 'warn' },
  deleted: { label: 'Deleted', tone: 'danger' },
};

export function kindLabel(c) {
  return { pdf: 'PDF', two_slot: 'Two-file', parametric: 'Blank' }[c.source_kind] || c.source_kind || '—';
}

/** The carton's flat size, in mm — what "size" means for a carton. */
export function sizeLabel(c) {
  const f = c.flat_size;
  if (!f || f.w_mm == null || f.h_mm == null) return '—';
  const mm = (x) => String(Math.round(Number(x) * 10) / 10);
  return `${mm(f.w_mm)} × ${mm(f.h_mm)} mm`;
}

export function duplicatesLabel(c) {
  const n = Array.isArray(c.duplicate_of) ? c.duplicate_of.length : 0;
  return n ? `${n} duplicate${n === 1 ? '' : 's'}` : '';
}

/** Which files can be opened. A deleted carton's files are kept, but only a
 *  live carton's can be fetched — restore it first. */
export function filesOf(c) {
  if (c.deleted_at) return [];
  const out = [];
  if (c.has_source) out.push({ which: 'source', label: c.source_kind === 'two_slot' ? 'Artwork PDF' : 'PDF' });
  if (c.has_die_file) out.push({ which: 'die', label: 'Die file' });
  return out;
}

/* ── the actions (#99) ────────────────────────────────────────────── */

export const ACTIONS = {
  unshare: {
    label: 'Unshare',
    done: 'Carton unshared',
    says: 'It becomes private to its owner. Their colleagues stop seeing it in their Library.',
  },
  delete: {
    label: 'Delete',
    done: 'Carton deleted',
    says: 'It leaves every Library. Its file is kept, so it can be restored.',
  },
  restore: {
    label: 'Restore',
    done: 'Carton restored',
    says: 'It comes back exactly as it was — private or shared.',
  },
};

/** The actions a carton offers, in the order the sheet lists them. */
export function actionsFor(c) {
  if (c.deleted_at) return ['restore'];
  return c.visibility === 'org' ? ['unshare', 'delete'] : ['delete'];
}

/** Why Save is not ready yet, in plain words; null when it is. */
export function actionProblem(action, reason) {
  if (!action) return 'Choose what to do.';
  if (!(reason || '').trim()) return 'Say why — the reason is kept with the change.';
  if (reason.trim().length > 500) return 'Keep the reason under 500 characters.';
  return null;
}

/** "51–100 of 528" — the backend's page and total, our 0-based page. */
export function pagerText({ page = 0, pageSize = PAGE, total = 0, shown = 0 } = {}) {
  if (!total) return 'No cartons';
  return `${page * pageSize + 1}–${page * pageSize + shown} of ${total}`;
}
